import React, { useEffect, useState } from "react";
import {
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { OilMonitor } from "../../api";
import { useGlobal } from "../../global/user";
import { Modal, Button, Form, Input, Switch} from "antd";

const initialState = {
  data: [],
  left: "dataMin",
  right: "dataMax",
  top: "dataMax+1",
  bottom: "dataMin-1",
  top2: "dataMax+20",
  bottom2: "dataMin-20",
  animation: true,
};

export default function OilMonitorChart({
  id,
  visible,
  setVisible,
  bounds,
  setBounds,
  makeUp,
  setMakeUp,
  A,
  B
}) {
  const { user } = useGlobal();
  const [state, setState] = useState(initialState);
  const [refLeft, setLeft] = useState("");
  const [refRight, setRight] = useState("");

  const [form] = Form.useForm();
  const { data, left, right } = state;
  useEffect(async () => {
    if (!visible) return;
    const chartDataRes = await OilMonitor.GetChartData(id, 14);
    if (chartDataRes.code === 200) {
      
      const processedData = chartDataRes.data.map((row) => {
        const time = new Date(row.time);
        row.voltage = parseFloat((row.voltage * A[0] + B[0]).toFixed(2))
        row.Rpressure = parseFloat((row.Rpressure * A[1] + B[1]).toFixed(2))
        row.Spressure = parseFloat((row.Spressure * A[2] + B[2]).toFixed(2))
        row.Tpressure = parseFloat((row.Tpressure * A[3] + B[3]).toFixed(2))
        row.time = time.getTime() - 1000 * 60 * 60 * 8; // this accounts for timezone offset
        return row;
      });
      
      setState((state) => ({
        ...state,
        data: processedData,
      }));
    }
    else alert("取得數值失敗");

    form.setFieldsValue({
      upper_bound: bounds.upperBound,
      lower_bound: bounds.lowerBound,
      tolerance: bounds.tolerance,
      makeUp: makeUp,
    });
  }, [visible]);

  const zoom = () => {
    if (refLeft === refRight) return;

    let [Left, Right] =
      refLeft <= refRight ? [refLeft, refRight] : [refRight, refLeft];

    setState((state) => ({
      ...state,
      left: Left,
      right: Right,
    }));
    setRight("");
    setLeft("");
  };

  const zoomOut = () => {
    setState((state) => ({
      ...state,
      left: "dataMin",
      right: "dataMax",
    }));
    setLeft("");
    setRight("");
  };

  const TimeFormat = (num) => {
    const { left, right } = state;
    const defaultFormat = new Date(num).toLocaleDateString();
    if (left === "dataMin" || right === "dataMax") return defaultFormat;
    if (right - left > 24 * 60 * 60 * 1000) return defaultFormat;
    return new Date(num).toLocaleString();
  };

  const onFinish = async () => {
    const { upper_bound, lower_bound, tolerance, make_up } = form.getFieldsValue();
    setBounds({
      upperBound: upper_bound,
      lowerBound: lower_bound,
      tolerance: tolerance,
    });
    setMakeUp(make_up ? 1 : 0);
    const updateBoundsRes = await OilMonitor.UpdateBounds(id, upper_bound, lower_bound, tolerance);
    if (updateBoundsRes.code === 200) {
      const updateMakeUpRes = await OilMonitor.UpdateMakeUp(id, make_up);
      if (updateMakeUpRes.code === 200) alert("成功更新油壓上下限");
      else alert("更新油壓上下限失敗！");
    } else alert("更新油壓上下限失敗！");
  };

  const footer = [
    <Form
      key="boundsForm"
      form={form}
      initialValues={{
        upper_bound: bounds.upperBound,
        lower_bound: bounds.lowerBound,
        tolerance: bounds.tolerance,
        makeUp: makeUp
      }}
      layout="inline"
      onFinish={onFinish}
    >
      <Form.Item
        name="upper_bound"
        label="油壓上限"
        rules={[{ required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="lower_bound"
        label="油壓下限"
        rules={[{ required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="tolerance"
        label="油壓下限放寬"
        hidden={user.name != "WebManager"}
        rules={[{ required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="make_up"
        label="折線圖補點"
        hidden={user.name != "WebManager"}
      >
        <Switch
          checkedChildren="開啟"
          unCheckedChildren="關閉"
          defaultChecked={makeUp}
        />
      </Form.Item>
    </Form>,
    <Button key="store" onClick={() => form.submit()}>
      儲存
    </Button>,
    <Button key="back" onClick={() => setVisible(false)}>
      返回
    </Button>,
    <Button key="reset" onClick={zoomOut}>
      重置
    </Button>,
  ];
  return (
    <Modal
      title="數位油壓數據"
      open={visible}
      onCancel={() => setVisible(false)}
      footer={footer}
      width={1000}
    >
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          width={800}
          height={400}
          data={data}
          onMouseDown={(e) => {
            if (e) setLeft(e.activeLabel);
          }}
          onMouseMove={(e) => e && refLeft !== "" && setRight(e.activeLabel)}
          onMouseUp={zoom}
        >
          <ReferenceLine
            y={bounds.lowerBound}
            label="油壓下限"
            stroke="red"
            yAxisId="1"
          />
          <ReferenceLine
            y={bounds.upperBound}
            label="油壓上限"
            stroke="red"
            yAxisId="1"
          />
          <XAxis
            allowDataOverflow
            dataKey="time"
            domain={[left, right]}
            type="number"
            tickFormatter={TimeFormat}
            tickCount={20}
          />
          <YAxis
            allowDataOverflow
            domain={[
              bounds.lowerBound - 0.5,
              bounds.upperBound + 0.5
            ]}
            tickFormatter={(value) => value.toFixed(2)}
            type="number"
            yAxisId="1"
            unit="bar"
          />
          <YAxis
            orientation="right"
            allowDataOverflow
            domain={[9, 13]}
            type="number"
            yAxisId="2"
            unit="V"
          />
          <Tooltip
            labelFormatter={(e) => new Date(e).toLocaleString()}
            position={{ x: 0, y: 0 }}
            contentStyle={{ fontSize: 7 }}
          />
          <Legend />
          <Line
            yAxisId="1"
            type="linear"
            dataKey="Rpressure"
            name="R相油壓"
            stroke="blue"
            unit="bar"
            dot={false}
            animationDuration={300}
          />
          <Line
            yAxisId="1"
            type="linear"
            dataKey="Spressure"
            name="S相油壓"
            unit="bar"
            stroke="green"
            dot={false}
            animationDuration={300}
          />
          <Line
            yAxisId="1"
            type="linear"
            dataKey="Tpressure"
            name="T相油壓"
            unit="bar"
            stroke="black"
            dot={false}
            animationDuration={300}
          />
          <Line
            yAxisId="2"
            dot={false}
            type="linear"
            name="電壓"
            dataKey="voltage"
            unit="V"
            stroke="grey"
            animationDuration={300}
          />
          {refLeft && refRight ? (
            <ReferenceArea
              yAxisId="1"
              x1={refLeft}
              x2={refRight}
              strokeOpacity={0.3}
            />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </Modal>
  );
}
